/**
 * External API health — the five APIs the monitor cron probes every five minutes
 * (`openai`, `anthropic`, `groq`, `google`, `pipedream`).
 *
 * Every code here is PLATFORM-scoped and keyed `(external_api, <api>)`: a provider's
 * account is shared by every tenant, so one broken provider is one row, never one per
 * organization. The three codes are three DIFFERENT facts about one API, so an API can hold
 * an outage row and a probe-fault row at once and neither can mask the other.
 *
 * A healthy probe resolves all three. A probe fault also resolves the outage, because a
 * provider that refused our request has, by definition, answered it.
 */
import type { IssueDefinition } from "../issue-definition";
import { ISSUE_CATEGORY } from "../issue-enums";

export const EXTERNAL_API_ISSUES = {
  /**
   * The provider stopped answering, or answered with a rejected credential.
   *
   * ## Pages
   *
   * Every customer call on this provider is at risk, and the 2026-08-05 Anthropic credit
   * exhaustion ran fifteen hours with nobody told. This is the one external-API fact a human
   * must hear before they next open the Issues page.
   *
   * `PROVIDER_TRANSIENT` reaches this code only on the SECOND consecutive failed sample: one
   * 503 in thirty-seven samples is a blip, not an outage.
   */
  EXTERNAL_API_OUTAGE: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "An external API the platform depends on stopped answering its monitor probe",
    severity: "high",
    page: true,
    remedy:
      "Open the row and read `rawPayload`: `api` names the provider, `verdict` is the classified failure (`AUTH_INVALID/ACCOUNT`, `ACCOUNT_CANNOT_SPEND/ACCOUNT`, `RATE_LIMITED/…`, `PROVIDER_TRANSIENT/…`, or `unclassified`), and `providerError` is the provider's own sentence, which usually names the fix. " +
      "`customerTraffic` is COUNTED from recorded generations over the last 15 minutes, never inferred: zero calls means the provider is down with nobody affected yet. " +
      "`AUTH_INVALID` — rotate or restore the key in the deployment's environment group. `ACCOUNT_CANNOT_SPEND` — top up or raise the spend limit on the provider console. `RATE_LIMITED` — our account is throttled; check the provider's usage page. `PROVIDER_TRANSIENT` — the provider is failing on its own; check its status page. " +
      "An `unclassified` verdict on a model provider is a provider sentence `classifyLlmFailure` does not recognise yet (apps/backend/src/_common/llm-failure/) — customer calls that hit it are not failed over until it is taught. " +
      "For `pipedream`, read the error as our own limiter's state: at the limit it QUEUES calls rather than failing them. " +
      "The issue resolves itself on the first healthy probe."
  },

  /**
   * The provider AUTHENTICATED us and then refused the monitor's request — a retired model id,
   * a payload it will not take. Customer traffic is unaffected; the monitor is blind.
   *
   * ## Does not page
   *
   * Nothing a customer touches is failing, and the fix is a code change made in working
   * hours. The groq and google probe faults of 2026-09 posted for days under the siren and
   * taught the channel to be skipped.
   */
  EXTERNAL_API_PROBE_FAULT: {
    category: ISSUE_CATEGORY.PROVIDER,
    message:
      "An external API's monitor probe is broken — the provider answered and refused the probe's request",
    severity: "medium",
    remedy:
      "The provider is fine; the probe is not. Read `providerError` in `rawPayload` — it is usually a retired model id or a payload the provider no longer accepts. " +
      "The probes live in apps/backend/src/admin/metrics/domain/services/external-api-monitor.service.ts, and their model ids resolve through the model catalog, so a refusal usually means the catalog entry rather than a constant in that file. " +
      "While this is open, the monitor cannot see a real outage of this provider. The issue resolves itself on the first healthy probe."
  },

  /**
   * No credential is configured for this provider, so the probe sent NO request.
   *
   * ## Does not page
   *
   * It is a configuration fact about this deployment, not a provider behaviour, and it never
   * changes until somebody sets a variable — seven of these reached the channel in two days
   * (NEX-4791) while every customer call was fine.
   */
  EXTERNAL_API_NOT_CONFIGURED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message:
      "An external API has no credential configured in this environment, so it was never asked",
    severity: "low",
    remedy:
      "Set the provider's API key in the deployment's environment group if it is meant to be available here; the variable is named in apps/backend/src/config/env/ai-providers.env.ts. " +
      "Any agent configured for this provider fails too, because the probe reads the same getter the product's client reads. " +
      "If the provider is deliberately not offered in this environment, ignore the issue. It resolves itself on the first healthy probe."
  }
} as const satisfies Record<string, IssueDefinition>;
